import { createBrowserRouter, redirect, type RouteObject } from "react-router";
import { lazy } from "react";
import { NotFoundPage, publicRouteTree } from "./publicRoutes";

// The public subtree (PublicLayout + the 14 pages) lives in publicRoutes.ts so
// the build-time prerender (src/entry-server.tsx) can import it without this
// module's createBrowserRouter call, which needs `window`.
export { publicRoutes } from "./publicRoutes";

// Layout shells and auth components — lazy-loaded for their code-splitting
// benefit, not to work around a "Vite optimizer deadlock" (that diagnosis
// was wrong; see docs/WEB_CLIENT_BLANK_SCREEN_FIX_PLAN.md for the real
// root cause and incident history).
const OidcCallback = lazy(() =>
  import("./auth/OidcCallback").then((m) => ({ default: m.OidcCallback }))
);
const ProtectedAppLayout = lazy(() =>
  import("@/components/layouts/ProtectedAppLayout").then((m) => ({ default: m.ProtectedAppLayout }))
);

// App pages
const DashboardPage = lazy(() => import("@/components/pages/DashboardPage"));
const SendMoneyPage = lazy(() => import("@/components/pages/SendMoneyPage"));
const TransactionHistoryPage = lazy(() => import("@/components/pages/TransactionHistoryPage"));
const TransactionDetailPage = lazy(() => import("@/components/pages/TransactionDetailPage"));
const RecipientsPage = lazy(() => import("@/components/pages/RecipientsPage"));
const EditRecipientPage = lazy(() => import("@/components/pages/EditRecipientPage"));
const AccountPage = lazy(() => import("@/components/pages/AccountPage"));
const EditProfilePage = lazy(() => import("@/components/pages/EditProfilePage"));
const ChangePasswordPage = lazy(() => import("@/components/pages/ChangePasswordPage"));
const PaymentSettingsPage = lazy(() => import("@/components/pages/PaymentSettingsPage"));
const ReferralsPage = lazy(() => import("@/components/pages/ReferralsPage"));
const InboxPage = lazy(() => import("@/components/pages/InboxPage"));
const DeleteSuccessPage = lazy(() => import("@/components/pages/DeleteSuccessPage"));
const GetAppPage = lazy(() => import("@/components/pages/GetAppPage"));

export const routes: RouteObject[] = [
  { path: "/callback", Component: OidcCallback },
  // Download QR code target: sends phones to their app store.
  { path: "/get-app", Component: GetAppPage },

  publicRouteTree,

  {
    path: "/app",
    Component: ProtectedAppLayout,
    children: [
      { index: true, loader: () => redirect("/app/dashboard") },
      { path: "dashboard", Component: DashboardPage },
      { path: "sendmoney", Component: SendMoneyPage },
      { path: "transaction-history", Component: TransactionHistoryPage },
      { path: "transactions/:id", Component: TransactionDetailPage },
      { path: "recipients", Component: RecipientsPage },
      { path: "recipients/:id/edit", Component: EditRecipientPage },
      { path: "personal", Component: AccountPage },
      { path: "edit-profile", Component: EditProfilePage },
      { path: "change-password", Component: ChangePasswordPage },
      { path: "payment-setting", Component: PaymentSettingsPage },
      { path: "referrals", Component: ReferralsPage },
      { path: "inbox", Component: InboxPage },
      // The in-app copies of the public help and legal pages were retired:
      // old links land on the single public page instead.
      { path: "faqs", loader: () => redirect("/faqs") },
      { path: "privacy-policys", loader: () => redirect("/privacy-policy") },
      { path: "security-policy", loader: () => redirect("/security-policy") },
      { path: "terms", loader: () => redirect("/terms-and-conditions") },
      { path: "terms-condition", loader: () => redirect("/terms-and-conditions") },
      { path: "delete-success", Component: DeleteSuccessPage },
    ],
  },

  { path: "*", Component: NotFoundPage },
];

export const router = createBrowserRouter(routes);
